import { Injectable } from '@angular/core';
import { ExportConfig, ExportOptions, ProjectDto } from '@api/projects/interfaces';
import { PermissionService } from '@app/services/permission/permission.service';
import * as Reducers from '@app/store/reducers';
import { IParticipantConfig, IProjectConfig } from '@interfaces/interfaces';
import { select, Store } from '@ngrx/store';
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import { catchError, distinctUntilChanged, filter, map, shareReplay, switchMap, tap } from 'rxjs/operators';
import * as SettingsActions from '../../store/settings/actions';
import { ProjectRestService } from '../rest/project-rest.service';

@Injectable({
	providedIn: 'root'
})
export class ProjectService {
	private readonly _activeProject$: Observable<ProjectDto>;
	private _activeProject: ProjectDto;
	private readonly _allProjects$: Observable<ProjectDto[]>;
	private _participantsConfigOfActiveProject: IParticipantConfig;
	private readonly _participantsConfig$: Observable<IParticipantConfig>;
	private _exportsConfigOfActiveProject: ExportConfig[];
	private readonly _exportsConfig$: Observable<ExportConfig[]>;

	private _refreshProjects$ = new BehaviorSubject<void>(undefined);

	constructor(
		private projectRestService: ProjectRestService,
		private store: Store,
		permissionService: PermissionService,
	) {
		this._allProjects$ = this._refreshProjects$.pipe(
			switchMap(() => store.pipe(select(Reducers.getUser))),
			switchMap(() => {
				return this.projectRestService.listProjects$().pipe(
					catchError(() => of([])),
				);
			}),
		);

		this._activeProject$ = this.store.select(Reducers.getActiveProject).pipe(
			filter(project => project !== undefined),
		);

		this.refreshProjects();

		this._participantsConfig$ = this._activeProject$.pipe(
			tap(activeProject => this._activeProject = activeProject),
			permissionService.checkPermission('participant-config:read'),
			switchMap(project => project ? this.projectRestService.getParticipantsConfig$(project.project_id) : of(undefined)),
			shareReplay({ bufferSize: 1, refCount : false })
		);

		this._participantsConfig$.subscribe(config => this._participantsConfigOfActiveProject = config);
		
		this._exportsConfig$ = combineLatest([this._activeProject$, permissionService.hasPermission$('exports:read')]).pipe(
			switchMap(([project, hasPermission]) => {
				if (project && hasPermission) {
					return this.projectRestService.getExportsConfig$(project.project_id).pipe(
						catchError(() => of([])),
					);
				} else {
					return of([]);
				}
			}),
			shareReplay({ bufferSize: 1, refCount : false })
		);

		this._activeProject$.subscribe(activeProject => this._activeProject = activeProject);
		this._exportsConfig$.subscribe(config => this._exportsConfigOfActiveProject = config);
	}

	public get activeProject$(): Observable<ProjectDto> {
		return this._activeProject$;
	}

	public get activeProject(): ProjectDto {
		return this._activeProject;
	}

	public setActiveProject(project: ProjectDto): void {
		this.store.dispatch(SettingsActions.activeProjectChange({ activeProjectID: project.project_id }));
	}

	public get projects$(): Observable<ProjectDto[]> {
		return this._allProjects$.pipe(
			distinctUntilChanged()
		);
	}

	public getProjectById(projectId: string): Observable<ProjectDto> {
		return this._allProjects$.pipe(
			map(array => array.filter(projectDto => projectDto.project_id == projectId).pop())
		);
	}
	
	public getProjectConfigById(projectId: string): Observable<IProjectConfig> {
		return this.projectRestService.projectConfig$(projectId);
	}

	public get participantsConfigOfActiveProject(): IParticipantConfig {
		return this._participantsConfigOfActiveProject;
	}

	public get participantsConfig$(): Observable<IParticipantConfig> {
		return this._participantsConfig$;
	}
	
	public get exportsConfigOfActiveProject(): ExportConfig[] {
		return this._exportsConfigOfActiveProject;
	}

	public get exportsConfig$(): Observable<ExportConfig[]> {
		return this._exportsConfig$;
	}

	public postExports$(projectID: number|string, exportId: string, exportOptions: ExportOptions): Observable<string> {
		return this.projectRestService.postExports$(projectID, exportId, exportOptions);
	}

	public async postExports(projectID: number|string, exportId: string, exportOptions: ExportOptions): Promise<string> {
		return this.projectRestService.postExports(projectID, exportId, exportOptions);
	}

	public createProject(projectDto: ProjectDto): Promise<ProjectDto> {
		return this.projectRestService.createProject(projectDto);
	}

	public refreshProjects(): void {
		this._refreshProjects$.next();
	}

	public downloadSelectedUserExports(exportConfig: ExportConfig, exportOptions: ExportOptions, fileName: string): void {
		this.postExports$(this.activeProject.project_id, exportConfig.exportId, exportOptions)
			.subscribe(data => {
				const blob = new Blob([data]);
				if (window.navigator.msSaveOrOpenBlob){
					window.navigator.msSaveBlob(blob, fileName);
				}
				else {
					const a = window.document.createElement('a');
					a.href = window.URL.createObjectURL(blob);
					a.download = fileName;
					document.body.appendChild(a);
					a.click();
					document.body.removeChild(a);
				}
			});
	}

	public updateProject$(projectDto: ProjectDto): Observable<ProjectDto> {
		if (projectDto.project_id === this._activeProject.project_id) {
			this.setActiveProject(projectDto);
		}
		return this.projectRestService.updateProject$(projectDto);
	}

	public uploadProjectArchive$(projectID: string, zipFile: File): Observable<ProjectDto> {
		return this.projectRestService.uploadProjectArchive(projectID, zipFile).pipe(tap(projectDto => {
			if (projectID === this._activeProject?.project_id) {
				projectDto.project_id = projectID;
				this.setActiveProject(projectDto);
			}
		}));
	}
	
	public projectUserPermissions$(projectID: string, projectUsername: string): Observable<string[]> {
		return this.projectRestService.projectUserPermissions$(projectID, projectUsername);
	}

	public projectUserPermissions(projectID: string, projectUsername: string): Promise<string[]> {
		return this.projectRestService.projectUserPermissions(projectID, projectUsername);
	}

	public get hasWorkflowWriteOnActiveProject$(): Observable<boolean> {
		return this.store.pipe(
			select(Reducers.getUser),
			switchMap(state => combineLatest([of(state), this.activeProject$])),
			switchMap(([state, project]) => this.projectUserPermissions$(project.project_id, state.username)),
			switchMap(permissions => of(permissions.includes('workflow:item:write')))
		);
	}
}

export interface ProjectUserDto {
	username: string;
	roles?: string[];
	group_ids?: string[];
}
